import { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  contentCollectionFormSelector,
  contentCollectionFormSubmissionRequiredSelector,
  setFormSubmissionRequiredAction,
} from '@air/feature-content-collection/redux';
import {
  ContentCollectionOnboardingEmptyState,
  ContentCollectionSidebar,
  ContentCollectionSidebarHeader,
  ContentCollectionSidebarInstructions,
  ContentCollectionSidebarSeparator,
  ContentCollectionSidebarTerms,
} from '@air/feature-content-collection/ui';
import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { ContentCollectionOnboardingForm } from '~/components/ContentCollection/ContentCollectionOnboardingForm';
import { Routes } from '~/constants/routes';
import { useOnMissingUserSession } from '~/hooks/useOnMissingUserSession';

export type ContentCollectionOnboardingViewProps = {
  requireAccount: boolean;
};

export const ContentCollectionOnboardingView = memo(({ requireAccount }: ContentCollectionOnboardingViewProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const dispatch = useDispatch();
  const form = useSelector(contentCollectionFormSelector);
  const formSubmissionRequired = useSelector(contentCollectionFormSubmissionRequiredSelector);

  const hasFormSubmissionRequirement = requireAccount || form.createSubmissionBoard;

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const onMissingSession = useCallback(() => {
    if (!isFormSubmitted) {
      dispatch(setFormSubmissionRequiredAction({ formSubmissionRequired: true }));
    }
  }, [dispatch, isFormSubmitted]);

  useOnMissingUserSession({
    onMissingSession,
    enabled: hasFormSubmissionRequirement,
  });

  const onFormSubmissionRequirementCompleted = useCallback(() => {
    setIsFormSubmitted(true);
    dispatch(setFormSubmissionRequiredAction({ formSubmissionRequired: false }));
  }, [dispatch]);

  return (
    <div
      className="flex size-full flex-col overflow-y-auto md:flex-row md:overflow-y-hidden"
      data-testid="CONTENT_COLLECTION_ONBOARDING_VIEW"
    >
      <ContentCollectionSidebar
        className={cn(
          'flex flex-1 items-center justify-center md:flex-none',
          !formSubmissionRequired && 'mb-12 md:mb-0',
        )}
      >
        <ContentCollectionSidebarHeader />
        {isAboveMediumScreen || !formSubmissionRequired ? (
          <>
            <ContentCollectionSidebarInstructions />
            <ContentCollectionSidebarTerms
              routes={{ terms: Routes.marketing.terms, privacy: Routes.marketing.privacy }}
            />
          </>
        ) : (
          <>
            <ContentCollectionSidebarSeparator />
            <ContentCollectionOnboardingForm
              requireAccount={requireAccount}
              onLoggedIn={onFormSubmissionRequirementCompleted}
              onCompletedForm={onFormSubmissionRequirementCompleted}
              className="p-0"
            />
          </>
        )}
      </ContentCollectionSidebar>

      <div className="flex w-full items-center justify-center p-4 empty:p-0">
        {formSubmissionRequired && isAboveMediumScreen ? (
          <ContentCollectionOnboardingForm
            requireAccount={requireAccount}
            onLoggedIn={onFormSubmissionRequirementCompleted}
            onCompletedForm={onFormSubmissionRequirementCompleted}
          />
        ) : null}
        {!formSubmissionRequired && <ContentCollectionOnboardingEmptyState />}
      </div>
    </div>
  );
});

ContentCollectionOnboardingView.displayName = 'ContentCollectionOnboardingView';
